import { Floorplan, FloorplanMarker } from '../types/floorplan';
import { SurveyProps } from '../types/survey';

export const FLOOR_PLANS_CONFIG_DOC_ID = 'floorPlans';

export type FloorPlanConfigByMarkerId = {
  [markerId: string]: FloorplanMarker;
};

export type FloorPlanConfigByFloor = {
  [floor: string]: {
    fileName?: string;
    lastModified?: number;
    url?: string;
    data?: string;
    width?: number;
    height?: number;
    floor?: string;
    markersById?: FloorPlanConfigByMarkerId;
  };
};

type SurveyFloorPlansConfigDoc = {
  floorPlansByFloor?: FloorPlanConfigByFloor;
};

type ResolveSurveyFloorPlansConfigArgs = {
  surveyDocData?: Partial<SurveyProps> | null;
  floorPlansConfigData?: SurveyFloorPlansConfigDoc | null;
  context?: string;
  surveyId?: string;
};

type ResolveSurveyFloorPlansConfigResult = {
  floorPlans: Floorplan[];
  floorPlansByFloor: FloorPlanConfigByFloor;
  source: 'config' | 'survey' | 'empty';
};

type FloorPlansConfigBatch = {
  set: (...args: any[]) => unknown;
};

function markerIdFor({
  marker,
  index,
}: {
  marker?: Partial<FloorplanMarker>;
  index: number;
}) {
  return `${marker?.id || `marker_${index}`}`.trim();
}

function markerHasAssignedRoom(
  marker?: Partial<FloorplanMarker> | null,
): boolean {
  return typeof marker?.roomId === 'string' && marker.roomId.trim().length > 0;
}

export function stripUnassignedFloorPlanMarkers(
  floorPlans?: Partial<Floorplan>[] | null,
): Partial<Floorplan>[] {
  return (floorPlans || []).filter(Boolean).map((floorPlan) => {
    return {
      ...floorPlan,
      markers: (floorPlan?.markers || []).filter((marker) => {
        return !!marker && markerHasAssignedRoom(marker);
      }),
    };
  });
}

export function floorPlansArrayToConfigMap(
  floorPlans?: Partial<Floorplan>[] | null,
): FloorPlanConfigByFloor {
  const next: FloorPlanConfigByFloor = {};

  (floorPlans || [])
    .filter(Boolean)
    .sort((a, b) =>
      `${a?.floor || ''}`.localeCompare(`${b?.floor || ''}`, undefined, {
        sensitivity: 'base',
      }),
    )
    .forEach((floorPlan) => {
      const floor = `${floorPlan?.floor || ''}`.trim();

      if (!floor) {
        return;
      }

      const markersById: FloorPlanConfigByMarkerId = {};

      (floorPlan?.markers || [])
        .filter(Boolean)
        .sort((a, b) =>
          markerIdFor({ marker: a, index: 0 }).localeCompare(
            markerIdFor({ marker: b, index: 0 }),
            undefined,
            { sensitivity: 'base' },
          ),
        )
        .forEach((marker, index) => {
          const markerId = markerIdFor({ marker, index });
          markersById[markerId] = {
            ...(marker as FloorplanMarker),
            id: markerId,
          };
        });

      const floorConfig: FloorPlanConfigByFloor[string] = {
        floor,
        markersById,
      };

      if (typeof floorPlan?.fileName !== 'undefined') {
        floorConfig.fileName = floorPlan.fileName;
      }
      if (typeof floorPlan?.lastModified !== 'undefined') {
        floorConfig.lastModified = floorPlan.lastModified;
      }
      if (typeof floorPlan?.url !== 'undefined') {
        floorConfig.url = floorPlan.url;
      }
      if (typeof floorPlan?.data !== 'undefined') {
        floorConfig.data = floorPlan.data;
      }
      if (typeof floorPlan?.width !== 'undefined') {
        floorConfig.width = floorPlan.width;
      }
      if (typeof floorPlan?.height !== 'undefined') {
        floorConfig.height = floorPlan.height;
      }

      next[floor] = floorConfig;
    });

  return next;
}

export function floorPlansConfigMapToArray(
  floorPlansByFloor?: FloorPlanConfigByFloor | null,
): Floorplan[] {
  if (!floorPlansByFloor || typeof floorPlansByFloor !== 'object') {
    return [];
  }

  return Object.entries(floorPlansByFloor)
    .sort(([floorA], [floorB]) =>
      `${floorA || ''}`.localeCompare(`${floorB || ''}`, undefined, {
        sensitivity: 'base',
      }),
    )
    .map(([floorKey, floorPlan]) => {
      const floor = `${floorPlan?.floor || floorKey || ''}`.trim();
      const markersById = floorPlan?.markersById || {};
      const markers = Object.entries(markersById)
        .sort(([markerA], [markerB]) =>
          `${markerA || ''}`.localeCompare(`${markerB || ''}`, undefined, {
            sensitivity: 'base',
          }),
        )
        .map(([markerId, marker]) => {
          return {
            ...(marker as FloorplanMarker),
            id: marker?.id || markerId,
          };
        });

      const floorPlanAsArray: Partial<Floorplan> = {
        floor,
        markers,
      };

      if (typeof floorPlan?.fileName !== 'undefined') {
        floorPlanAsArray.fileName = floorPlan.fileName;
      }
      if (typeof floorPlan?.lastModified !== 'undefined') {
        floorPlanAsArray.lastModified = floorPlan.lastModified;
      }
      if (typeof floorPlan?.url !== 'undefined') {
        floorPlanAsArray.url = floorPlan.url;
      }
      if (typeof floorPlan?.data !== 'undefined') {
        floorPlanAsArray.data = floorPlan.data;
      }
      if (typeof floorPlan?.width !== 'undefined') {
        floorPlanAsArray.width = floorPlan.width;
      }
      if (typeof floorPlan?.height !== 'undefined') {
        floorPlanAsArray.height = floorPlan.height;
      }

      return floorPlanAsArray as Floorplan;
    })
    .filter((floorPlan) => !!floorPlan.floor);
}

function hasValidConfigFloorPlans(
  floorPlansConfigData?: SurveyFloorPlansConfigDoc | null,
): boolean {
  const floorPlansByFloor = floorPlansConfigData?.floorPlansByFloor;

  return (
    !!floorPlansConfigData &&
    typeof floorPlansConfigData === 'object' &&
    !!floorPlansByFloor &&
    typeof floorPlansByFloor === 'object' &&
    !Array.isArray(floorPlansByFloor)
  );
}

function hasValidSurveyFloorPlans(
  surveyDocData?: Partial<SurveyProps> | null,
): boolean {
  return Array.isArray(surveyDocData?.floorPlans);
}

export function resolveSurveyFloorPlansConfig({
  surveyDocData,
  floorPlansConfigData,
}: ResolveSurveyFloorPlansConfigArgs): ResolveSurveyFloorPlansConfigResult {
  const surveyFloorPlans = Array.isArray(surveyDocData?.floorPlans)
    ? surveyDocData.floorPlans
    : [];
  const surveyFloorPlansByFloor = floorPlansArrayToConfigMap(surveyFloorPlans);

  const configFloorPlans = floorPlansConfigMapToArray(
    floorPlansConfigData?.floorPlansByFloor,
  );
  const configFloorPlansByFloor = floorPlansArrayToConfigMap(configFloorPlans);

  const configValid = hasValidConfigFloorPlans(floorPlansConfigData);
  const surveyValid = hasValidSurveyFloorPlans(surveyDocData);

  if (configValid) {
    return {
      floorPlans: configFloorPlans,
      floorPlansByFloor: configFloorPlansByFloor,
      source: 'config',
    };
  }

  if (surveyValid) {
    return {
      floorPlans: surveyFloorPlans,
      floorPlansByFloor: surveyFloorPlansByFloor,
      source: 'survey',
    };
  }

  return {
    floorPlans: [],
    floorPlansByFloor: {},
    source: 'empty',
  };
}

export function queueFloorPlansConfigDocWrite({
  batch,
  floorPlansConfigDocRef,
  floorPlansByFloor,
  lastUpdated,
}: {
  batch: FloorPlansConfigBatch;
  floorPlansConfigDocRef: unknown;
  floorPlansByFloor: FloorPlanConfigByFloor;
  lastUpdated: Record<string, unknown>;
}) {
  batch.set(floorPlansConfigDocRef, {
    floorPlansByFloor,
    ...lastUpdated,
  });
}
